import { useQueryClient } from '@tanstack/react-query';
import {
    ButtonSizes,
    ButtonVariants,
    IconNames,
    Link,
    Menu,
    Stepper,
} from '@triumph/design-system';
import { orderBy, startCase } from 'es-toolkit';
import { Checkbox } from 'primereact/checkbox';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useFadingHighlight } from '@/components/hooks/useFadingHighlight';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { useSubmitInternalNoteMutation } from '@/components/shared/api/mutations/applicant';
import { useApplicantActivityQuery } from '@/components/shared/api/queries/applicant';
import { Divider } from '@/components/shared/components/common.StyledComponents';
import { DDDCardTitle } from '../../Applicant.StyledComponents';
import { useMentionSuggestions } from '@/components/shared/components/MentionTextarea';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';
import { getDateFromUtcString } from '@/components/shared/utils/utils';
import { useOktaAuth } from '@okta/okta-react';

import { getNotificationHtml } from '@/components/shared/api/endpoints/applicant';

import {
    ActivityInformationRequest,
    ActivityNote,
    ActivityPageVisit,
    ActivityProductStatus,
    ApplicantEmail,
    ApplicantNoteType,
    ApplicantTabId,
} from '../../Applicant.types';
import { ActivityNoteForm } from './ActivityNoteForm';
import { buildSandboxedEmailDocument, sanitizeEmailHtml } from './buildSandboxedEmailDocument';
import {
    Container,
    Details,
    DeviceChangeBadge,
    EmailHeaderLine,
    EmptySelectionMessage,
    FilterButtonsWrapper,
    FilterMenuList,
    FilterMenuRow,
    FilterMenuRowLabel,
    ActivityCard,
    ActivityCardBody,
    ActivityCardHeader,
    HighlightAnchor,
    InfoRequestField,
    InfoRequestFieldLabel,
    InfoRequestFieldValue,
    PageVisitDetails,
    ResetFilterButton,
    Status,
    StepperWrapper,
    SubmissionLinkButton,
    ViewEmailLink,
} from './ApplicantActivity.StyledComponents';

/**
 * The four activity categories. "All" is not a category — it is simply "every
 * category ticked". This enum is the single source of truth for category
 * keys/labels, consumed by both the checkbox-menu rows and the union filter.
 */
export enum ActivityCategory {
    ProductStatuses = 'Product Statuses',
    PageVisits = 'Page Visits',
    InternalNotes = 'Internal Notes',
    OriginationsAutomation = 'Originations Automation',
    AutomatedEmails = 'Automated Emails',
    InformationRequests = 'Information Requests',
}

/** Display order for the checkbox-menu rows and the "All" select-all set. */
export const ACTIVITY_CATEGORIES: ActivityCategory[] = [
    ActivityCategory.ProductStatuses,
    ActivityCategory.PageVisits,
    ActivityCategory.InternalNotes,
    ActivityCategory.OriginationsAutomation,
    ActivityCategory.AutomatedEmails,
    ActivityCategory.InformationRequests,
];

/** Categories active on first render and after "Reset Filters". */
const DEFAULT_ACTIVE_CATEGORIES: ActivityCategory[] = [
    ActivityCategory.InternalNotes,
    ActivityCategory.OriginationsAutomation,
    ActivityCategory.AutomatedEmails,
    ActivityCategory.InformationRequests,
];

const renderDescription = ({
    status,
    email,
    sourceSystem,
    description,
}: {
    status: string;
    email: string;
    sourceSystem?: string;
    description?: string;
}) => {
    return (
        <>
            <Status>{status}</Status>
            {email ? (
                <Link href={`mailto:${email}`} data-testid="reviewer-email">
                    {email}
                </Link>
            ) : null}
            {sourceSystem ? <span> via {sourceSystem}</span> : null}
            {description ? <Details>{description}</Details> : null}
        </>
    );
};

// Print passes no onViewSubmission, so the report never shows a link it cannot follow.
const mappedProductStatuses = (
    productStatuses: ActivityProductStatus[],
    onViewSubmission?: (applicantSubmissionId: string) => void,
) => {
    return productStatuses.map(
        ({ product, status, details, email, sourceSystem, timestampAdded, applicantSubmissionId }) => {
            return {
                label: product,
                description: (
                    <>
                        {renderDescription({
                            status: `Status changed to ${status}`,
                            email: email ?? '',
                            sourceSystem: sourceSystem ?? undefined,
                            description: details,
                        })}
                        {applicantSubmissionId && onViewSubmission ? (
                            <SubmissionLinkButton
                                type="button"
                                data-testid="view-submission-link"
                                onClick={() => onViewSubmission(applicantSubmissionId)}
                            >
                                View submission
                            </SubmissionLinkButton>
                        ) : null}
                    </>
                ),
                timestamp: getDateFromUtcString(timestampAdded),
                icon: IconNames.User_AccountAvatar,
            };
        },
    );
};

const mappedPageVisits = (pageVisits: ActivityPageVisit[]) => {
    return pageVisits.map((visit, i) => {
        const {
            applicantReferrer,
            domain,
            pageVisited,
            rawUrl,
            timestampAdded,
            deviceType,
            deviceBrowser,
            deviceOs,
            ipAddress,
        } = visit;

        let urlDetailDisplay = (
            <PageVisitDetails className="page-visit-wrap">{domain}</PageVisitDetails>
        );
        if (rawUrl) {
            const queryIndex = rawUrl.indexOf('?');
            let displayUrl = domain + rawUrl;
            if (queryIndex >= 0) {
                const base = domain + rawUrl.slice(0, queryIndex);
                const params = rawUrl
                    .slice(queryIndex + 1)
                    .split('&')
                    .join('\n&');
                displayUrl = `${base}\n?${params}`;
            }
            urlDetailDisplay = (
                <PageVisitDetails className="page-visit-wrap">{displayUrl}</PageVisitDetails>
            );
        }

        // Page visits arrive newest-first, so the chronologically-previous visit is the next
        // element. Flag only a genuine device-type change; skip when either side is unknown.
        const previousVisit = pageVisits[i + 1];
        const deviceChanged = Boolean(
            previousVisit &&
                deviceType &&
                previousVisit.deviceType &&
                deviceType !== previousVisit.deviceType,
        );

        return {
            label: 'APPLICANT',
            description: (
                <>
                    Applicant viewed the page {pageVisited}
                    {urlDetailDisplay}
                    <PageVisitDetails className="page-visit-wrap">-----</PageVisitDetails>
                    <PageVisitDetails className="page-visit-wrap">
                        Referrer: {applicantReferrer || '-'}
                    </PageVisitDetails>
                    <PageVisitDetails className="page-visit-wrap">
                        Device Type: {deviceType || '-'}
                    </PageVisitDetails>
                    <PageVisitDetails className="page-visit-wrap">
                        Browser: {deviceBrowser || '-'}
                    </PageVisitDetails>
                    <PageVisitDetails className="page-visit-wrap">
                        OS: {deviceOs || '-'}
                    </PageVisitDetails>
                    <PageVisitDetails className="page-visit-wrap">
                        IP: {ipAddress || '-'}
                    </PageVisitDetails>
                    {deviceChanged ? (
                        <DeviceChangeBadge data-testid="device-change-badge">
                            ⇄ Device changed from {previousVisit.deviceType} to {deviceType}
                        </DeviceChangeBadge>
                    ) : null}
                </>
            ),
            timestamp: getDateFromUtcString(timestampAdded),
            icon: IconNames.User_AccountAvatar,
        };
    });
};

const mappedInternalNotes = (notes: ActivityNote[], highlightNoteId: string | null) => {
    return notes.map(({ id, note, email, timestampAdded, applicantNoteType }) => {
        const isOriginationsAutomation =
            applicantNoteType === ApplicantNoteType.OriginationsAutomation;

        return {
            label: startCase(applicantNoteType),
            description: (
                <HighlightAnchor data-applicant-note-id={id} isFocused={id === highlightNoteId}>
                    {renderDescription({
                        status: note,
                        email: isOriginationsAutomation
                            ? email?.includes('@')
                                ? email
                                : ''
                            : email,
                    })}
                </HighlightAnchor>
            ),
            timestamp: getDateFromUtcString(timestampAdded),
            icon: IconNames.Filetype_Document,
        };
    });
};

const SPINNER_DOCUMENT = `<!doctype html><html><head><title>Loading email…</title><style>
    body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;}
    .spinner{width:40px;height:40px;border:4px solid #ccc;border-top-color:#333;border-radius:50%;animation:spin 0.8s linear infinite;}
    @keyframes spin{to{transform:rotate(360deg);}}
</style></head><body><div class="spinner" role="status" aria-label="Loading"></div></body></html>`;

const ERROR_DOCUMENT = `<!doctype html><html><head><title>Unable to load email</title><style>
    body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;padding:0 24px;text-align:center;}
    h2{font-size:1.75rem;font-weight:600;color:#333;margin:0;}
</style></head><body><h2>Unable to load this email. Please try again later.</h2></body></html>`;

// Opens the email HTML in a new tab: shows a spinner immediately (so the tab
// isn't blank while fetching), then swaps in the sanitized body. window.open must
// run synchronously in the click handler to survive popup blockers.
const openEmailInNewTab = async (
    applicantId: string,
    notificationId: string,
    isSafeEmailViewsEnabled: boolean,
) => {
    const tab = window.open('', '_blank');
    if (!tab) {
        // Popup blocked — surface rather than fail silently.
        window.alert('Please allow pop-ups to view the email.');
        return;
    }
    if (isSafeEmailViewsEnabled) tab.opener = null;
    tab.document.write(SPINNER_DOCUMENT);
    tab.document.close();

    try {
        const html = await getNotificationHtml(applicantId, notificationId);
        tab.document.open();
        tab.document.write(
            isSafeEmailViewsEnabled ? buildSandboxedEmailDocument(html) : sanitizeEmailHtml(html),
        );
        tab.document.close();
    } catch {
        tab.document.open();
        tab.document.write(ERROR_DOCUMENT);
        tab.document.close();
    }
};

const mappedEmails = (emails: ApplicantEmail[], onViewEmail: (notificationId: string) => void) => {
    return emails.map((email) => {
        return {
            label: 'AUTOMATED EMAIL',
            description: (
                <>
                    <Status>{email.subject}</Status>
                    {email.to.length ? (
                        <EmailHeaderLine>To: {email.to.join(', ')}</EmailHeaderLine>
                    ) : null}
                    {email.from ? <EmailHeaderLine>From: {email.from}</EmailHeaderLine> : null}
                    {email.cc.length ? (
                        <EmailHeaderLine>CC: {email.cc.join(', ')}</EmailHeaderLine>
                    ) : null}
                    {email.bcc.length ? (
                        <EmailHeaderLine>BCC: {email.bcc.join(', ')}</EmailHeaderLine>
                    ) : null}
                    {email.sourceSystem ? <span> via {email.sourceSystem}</span> : null}
                    {email.isSecureCode ? null : (
                        <div>
                            <ViewEmailLink
                                type="button"
                                data-testid={`view-email-${email.notificationId}`}
                                onClick={() => onViewEmail(email.notificationId)}
                            >
                                {`View email \u{29C9}`}
                            </ViewEmailLink>
                        </div>
                    )}
                </>
            ),
            timestamp: getDateFromUtcString(email.timestampAdded),
            icon: IconNames.Office_Mail,
        };
    });
};

const mappedInformationRequests = (
    informationRequests: ActivityInformationRequest[],
    highlightNmiId: string | null,
) => {
    const replyRowIds = new Set(
        informationRequests.filter((request) => request.isReply).map((request) => request.id),
    );

    return informationRequests.map(
        ({ id, moreInfoType, author, message, product, timestampAdded, isReply }) => {
            const moreInfoTypeLabel =
                moreInfoType === 'DocsOnly'
                    ? 'Docs Only'
                    : moreInfoType === 'FullApp'
                      ? 'Full App'
                      : null;
            const isAnchor = isReply || !replyRowIds.has(id);
            return {
                label: isReply ? 'Information Request Responded To' : 'Information Request Sent',
                description: (
                    <HighlightAnchor
                        data-information-request-id={isAnchor ? id : undefined}
                        isFocused={isAnchor && id === highlightNmiId}
                    >
                        <InfoRequestField>
                            <InfoRequestFieldLabel>Product:</InfoRequestFieldLabel>
                            <InfoRequestFieldValue data-testid="ir-product">
                                {product}
                            </InfoRequestFieldValue>
                        </InfoRequestField>
                        {moreInfoTypeLabel ? (
                            <InfoRequestField>
                                <InfoRequestFieldLabel>Type:</InfoRequestFieldLabel>
                                <InfoRequestFieldValue data-testid="ir-type">
                                    {moreInfoTypeLabel}
                                </InfoRequestFieldValue>
                            </InfoRequestField>
                        ) : null}
                        <InfoRequestField>
                            <InfoRequestFieldLabel>Message:</InfoRequestFieldLabel>
                            <InfoRequestFieldValue data-testid="ir-message">
                                {message}
                            </InfoRequestFieldValue>
                        </InfoRequestField>
                        {author ? (
                            <InfoRequestField>
                                <InfoRequestFieldLabel>Author:</InfoRequestFieldLabel>
                                <InfoRequestFieldValue>
                                    <Link href={`mailto:${author}`} data-testid="ir-author">
                                        {author}
                                    </Link>
                                </InfoRequestFieldValue>
                            </InfoRequestField>
                        ) : null}
                    </HighlightAnchor>
                ),
                timestamp: getDateFromUtcString(timestampAdded),
                icon: IconNames.Filetype_Document,
            };
        },
    );
};

// Stepper only renders its left timestamp column (which carries the add-note
// form's "Now") when at least one step has a timestamp. When the filter excludes
// everything we still want that column, so the empty-state message is rendered as
// a single step with an intentionally invalid timestamp — truthy enough to keep
// the column, but it prints no date of its own.
const EMPTY_SELECTION_STEP = {
    description: (
        <EmptySelectionMessage data-testid="activity-empty-selection">
            No activity types selected
        </EmptySelectionMessage>
    ),
    timestamp: new Date(NaN),
    icon: IconNames.Interface_FilterOutlined,
};

interface ApplicantActivityProps {
    // Whether the Activity tab is the one currently showing. The design-system
    // Tabs keeps every tab's content mounted and only toggles CSS display, so a
    // plain mount effect can't detect re-entering this tab — this prop can.
    isActive?: boolean;
    focusNoteId?: string | null;
    onNoteFocused?: () => void;
    focusNmiId?: string | null;
    onNmiFocused?: () => void;
    // Print gets its own mount (see PrintAggregate), so this never touches the
    // live tab's filter state — it just seeds this copy with every category.
    isPrintView?: boolean;
}

export const ApplicantActivity = forwardRef<HTMLDivElement, ApplicantActivityProps>(
    (
        {
            isActive,
            focusNoteId = null,
            onNoteFocused,
            focusNmiId = null,
            onNmiFocused,
            isPrintView = false,
        },
        ref,
    ) => {
        const queryClient = useQueryClient();
        const { applicantId } = useApplicantContext();
        const { hasFeature } = usePermissions();
        const { isSubmissionsTabEnabled, isSafeEmailViewsEnabled } = useFeatureFlags();
        const { authState } = useOktaAuth();
        const userEmail = useCurrentUserEmail();
        const [searchParams, setSearchParams] = useSearchParams();

        const goToSubmission = useCallback(
            (applicantSubmissionId: string) => {
                const next = new URLSearchParams(searchParams);
                next.set('tab', ApplicantTabId.Application);
                next.set('submissionId', applicantSubmissionId);
                setSearchParams(next);
            },
            [searchParams, setSearchParams],
        );

        // The Menu has no open/defaultOpen prop — its open state is internal,
        // driven by a mousedown listener that toggles. Dispatch one whenever this
        // tab becomes active (not just on mount, since the tab content stays
        // mounted while hidden) so the filter is revealed every time a reviewer
        // opens the tab. Skip if already open (Menu sets data-open) — StrictMode's
        // double effect-invoke would otherwise toggle it straight back closed.
        const filterMenuRef = useRef<HTMLButtonElement>(null);
        useEffect(() => {
            if (!isActive) return;
            const button = filterMenuRef.current;
            if (!button || button.hasAttribute('data-open')) return;
            button.dispatchEvent(
                new MouseEvent('mousedown', { bubbles: true, cancelable: true, button: 0 }),
            );
        }, [isActive]);

        const stepperRef = useRef<HTMLDivElement>(null);
        const [highlightedNoteId, setHighlightedNoteId] = useFadingHighlight();
        const [highlightedNmiId, setHighlightedNmiId] = useFadingHighlight();

        // In-memory only; every visit resets to the default category set. A fresh
        // copy keeps the shared constant from being aliased as mutable state.
        // Print always gets every category — the report can't omit what the
        // reviewer happened to have filtered out on screen.
        const [activeCategories, setActiveCategories] = useState<ActivityCategory[]>(
            isPrintView ? [...ACTIVITY_CATEGORIES] : [...DEFAULT_ACTIVE_CATEGORIES],
        );

        const isEmptySelection = activeCategories.length === 0;

        // Drives the "All" select-all row: checked when every category is active,
        // unchecked when none are, indeterminate for any partial subset.
        const isAllSelected = activeCategories.length === ACTIVITY_CATEGORIES.length;
        const isPartialSelection = !isAllSelected && !isEmptySelection;

        const {
            data: activityData,
            isLoading: isActivityLoading,
            isLoadingError: isActivityLoadingError,
            refetch: refetchActivity,
        } = useApplicantActivityQuery({
            applicantId: applicantId ?? '',
        });

        const mentionSuggestions = useMentionSuggestions(applicantId ?? '');

        const { mutateAsync: submitNote, isPending: isSubmitNotePending } =
            useSubmitInternalNoteMutation();

        const applicantActivities = useMemo(() => {
            if (isEmptySelection) return [EMPTY_SELECTION_STEP];
            if (!activityData) return [];

            const activeSet = new Set(activeCategories);

            const allNotes = activityData.notes || [];
            const internalNotes = allNotes.filter(
                (note) => note.applicantNoteType === ApplicantNoteType.InternalNote,
            );
            const originationsAutomationNotes = allNotes.filter(
                (note) => note.applicantNoteType === ApplicantNoteType.OriginationsAutomation,
            );

            const activities = [
                ...(activeSet.has(ActivityCategory.ProductStatuses)
                    ? mappedProductStatuses(
                          activityData.productStatuses || [],
                          isPrintView || !isSubmissionsTabEnabled ? undefined : goToSubmission,
                      )
                    : []),
                ...(activeSet.has(ActivityCategory.PageVisits)
                    ? mappedPageVisits(activityData.pageVisits || [])
                    : []),
                ...(activeSet.has(ActivityCategory.InternalNotes)
                    ? mappedInternalNotes(internalNotes, highlightedNoteId)
                    : []),
                ...(activeSet.has(ActivityCategory.OriginationsAutomation)
                    ? mappedInternalNotes(originationsAutomationNotes, highlightedNoteId)
                    : []),
                ...(activeSet.has(ActivityCategory.AutomatedEmails)
                    ? mappedEmails(activityData.emails || [], (notificationId) =>
                          openEmailInNewTab(
                              applicantId ?? '',
                              notificationId,
                              isSafeEmailViewsEnabled,
                          ),
                      )
                    : []),
                ...(activeSet.has(ActivityCategory.InformationRequests)
                    ? mappedInformationRequests(
                          activityData.informationRequests || [],
                          highlightedNmiId,
                      )
                    : []),
            ];

            return orderBy(activities, ['timestamp'], ['desc']);
        }, [
            activityData,
            activeCategories,
            applicantId,
            highlightedNoteId,
            highlightedNmiId,
            isPrintView,
            isSubmissionsTabEnabled,
            isSafeEmailViewsEnabled,
            goToSubmission,
        ]);

        useEffect(() => {
            if (!focusNoteId) return;
            let raf2 = 0;
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    const node = stepperRef.current?.querySelector(
                        `[data-applicant-note-id="${focusNoteId}"]`,
                    );
                    if (!node) return;
                    setHighlightedNoteId(focusNoteId);
                    node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                    onNoteFocused?.();
                });
            });
            return () => {
                cancelAnimationFrame(raf1);
                cancelAnimationFrame(raf2);
            };
        }, [focusNoteId, applicantActivities, onNoteFocused]);

        useEffect(() => {
            if (!focusNmiId) return;
            let raf2 = 0;
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    const node = stepperRef.current?.querySelector(
                        `[data-information-request-id="${focusNmiId}"]`,
                    );
                    if (!node) return;
                    setHighlightedNmiId(focusNmiId);
                    node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                    onNmiFocused?.();
                });
            });
            return () => {
                cancelAnimationFrame(raf1);
                cancelAnimationFrame(raf2);
            };
        }, [focusNmiId, applicantActivities, onNmiFocused]);

        // Toggle a single category in/out of the active set.
        const handleCategoryToggle = (category: ActivityCategory) => {
            const activeSet = new Set(activeCategories);
            if (activeSet.has(category)) {
                activeSet.delete(category);
            } else {
                activeSet.add(category);
            }
            // Preserve the canonical category order rather than insertion order.
            setActiveCategories(ACTIVITY_CATEGORIES.filter((c) => activeSet.has(c)));
        };

        // The "All" row is a derived select-all: when everything is already active
        // it clears to the empty set, otherwise it selects every category. It is a
        // UI affordance, not a category.
        const handleToggleAll = () =>
            setActiveCategories(isAllSelected ? [] : [...ACTIVITY_CATEGORIES]);

        const handleReset = () => setActiveCategories([...DEFAULT_ACTIVE_CATEGORIES]);

        // Reset is a no-op when the selection already matches the default set.
        const isDefaultSelection =
            activeCategories.length === DEFAULT_ACTIVE_CATEGORIES.length &&
            DEFAULT_ACTIVE_CATEGORIES.every((c) => activeCategories.includes(c));

        // PrimeReact's Checkbox has no indeterminate prop, so set it on the native
        // input directly for the "mixed" (partial selection) state of the "All" row.
        const allInputRef = useCallback(
            (input: HTMLInputElement | null) => {
                if (input) input.indeterminate = isPartialSelection;
            },
            [isPartialSelection],
        );

        const canAddNotes = hasFeature(Feature.AddNotes);

        const handleAddNote = async ({ note }: { note: string }) => {
            if (note && applicantId && !isSubmitNotePending) {
                const userId = authState?.idToken?.claims?.sub;

                honeybadgerLogEvent(EVENT_LOG_NAMES.AddInternalNoteClick, {
                    level: 'INFO',
                    customAttributes: {
                        ...getLoggingContext(userEmail, userId),
                        currentPage: window.location.pathname,
                        timestamp: new Date().toISOString(),
                        applicantId,
                    },
                });

                await submitNote({ applicantId, note });
                queryClient.invalidateQueries({
                    queryKey: ['applicant-activity', applicantId],
                });
            }
        };

        const filterControls = (
            <FilterButtonsWrapper>
                <Menu
                    label="Activity Filter"
                    variant={ButtonVariants.secondary}
                    buttonSize={ButtonSizes.small}
                    icon={IconNames.Arrow_CaretDown}
                    iconRight
                    menuTestId="activity-filter"
                    menuForwardedRef={filterMenuRef}
                >
                    <FilterMenuList>
                        <FilterMenuRow htmlFor="activity-filter-all">
                            <Checkbox
                                inputId="activity-filter-all"
                                inputRef={allInputRef}
                                data-testid="activity-filter-all"
                                // Filled box for both full and partial selections; a
                                // partial set shows a minus dash, a full set the check.
                                checked={!isEmptySelection}
                                icon={
                                    isPartialSelection
                                        ? () => <span className="pi pi-minus" />
                                        : undefined
                                }
                                onChange={handleToggleAll}
                            />
                            <FilterMenuRowLabel>All</FilterMenuRowLabel>
                        </FilterMenuRow>
                        {ACTIVITY_CATEGORIES.map((category) => {
                            // Category labels contain spaces; build a valid id once.
                            const inputId = `activity-filter-${category.replace(/\s+/g, '-')}`;
                            return (
                                <FilterMenuRow key={category} htmlFor={inputId}>
                                    <Checkbox
                                        inputId={inputId}
                                        data-testid={`activity-filter-${category}`}
                                        checked={activeCategories.includes(category)}
                                        onChange={() => handleCategoryToggle(category)}
                                    />
                                    <FilterMenuRowLabel>{category}</FilterMenuRowLabel>
                                </FilterMenuRow>
                            );
                        })}
                    </FilterMenuList>
                </Menu>
                <Divider />
                <ResetFilterButton
                    variant={ButtonVariants.tertiary}
                    size={ButtonSizes.small}
                    disabled={isDefaultSelection}
                    onClick={handleReset}
                >
                    Reset Filters
                </ResetFilterButton>
            </FilterButtonsWrapper>
        );

        return (
            <Container id="printSection" ref={ref} data-testid="applicant-activity">
                <ActivityCard>
                    {/* Print labels this section itself; a header would repeat it. */}
                    {isPrintView ? null : (
                        <ActivityCardHeader>
                            <DDDCardTitle>Activity</DDDCardTitle>
                            {filterControls}
                        </ActivityCardHeader>
                    )}
                    <ActivityCardBody>
                        <span title={!canAddNotes ? PERMISSION_DENIED_TITLE : undefined}>
                            {/*
                             * The Stepper unconditionally injects its own AddNewNote (a
                             * plain <textarea>) as its first item, which we can't extend
                             * with mention typeahead. We can't disable that injection, so we
                             * hide it via StepperWrapper and render our own mention-aware
                             * ActivityNoteForm just above the timeline. Hiding (rather than
                             * removing) the injected item keeps the activity feed's item
                             * count unchanged.
                             */}
                            <StepperWrapper ref={stepperRef}>
                                {isPrintView ? null : (
                                    <ActivityNoteForm
                                        onSubmit={handleAddNote}
                                        disabled={!canAddNotes}
                                        loading={isSubmitNotePending}
                                        placeholder={
                                            canAddNotes ? 'Type your internal note here...' : ' '
                                        }
                                        suggestions={mentionSuggestions}
                                    />
                                )}
                                <TabDataState
                                    isLoading={isActivityLoading && !isEmptySelection}
                                    isLoadingError={isActivityLoadingError}
                                    onRetry={refetchActivity}
                                    errorMessage="Could not load activity."
                                >
                                    <Stepper steps={applicantActivities} />
                                </TabDataState>
                            </StepperWrapper>
                        </span>
                    </ActivityCardBody>
                </ActivityCard>
            </Container>
        );
    },
);
